import { useSecurity } from '@talos/kyoko';
import { useMemo } from 'react';
import type { PreTradeResponseData } from '../../redux/preTradeApiTypes';

interface UsePreTradeHeaderSymbolsResult {
  /** True when the header should render a symbol icon. */
  showSymbolIcon: boolean;
  /** Base currency for the symbol icon in single-symbol sessions. */
  symbolIconBaseCurrency?: string;
  /** Deduplicated session symbols rendered as a comma-separated label. */
  symbolList: string;
}

/**
 * Computes display metadata for the Pre-Trade panel header symbol section.
 *
 * Behavior:
 * - Single-symbol session: show icon (when base currency is available) and symbol text.
 * - Multi-symbol session: hide icon and show deduplicated symbols as a comma-separated label.
 */
export function usePreTradeHeaderSymbols(scenarios: PreTradeResponseData[]): UsePreTradeHeaderSymbolsResult {
  const sessionSymbols = useMemo(() => scenarios.map(scenario => scenario.Symbol), [scenarios]);
  const uniqueSymbols = useMemo(() => Array.from(new Set(sessionSymbols)), [sessionSymbols]);
  const isMultiSymbolSession = uniqueSymbols.length > 1;
  const singleSymbol = uniqueSymbols[0];
  const security = useSecurity(isMultiSymbolSession ? undefined : singleSymbol);

  return {
    showSymbolIcon: !isMultiSymbolSession,
    symbolIconBaseCurrency: security?.BaseCurrency,
    symbolList: uniqueSymbols.join(', '),
  };
}
